import axios from 'axios';
import { getOrCreateWebDeviceId } from '../utils/deviceId';
import { clearStoredAuth, emitAuthRedirect } from '../utils/authRedirect';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'X-Client-Platform': 'web',
  },
});

// Add token to requests
api.interceptors.request.use(
  async (config) => {
    const accessToken = localStorage.getItem('accessToken');
    const deviceId = getOrCreateWebDeviceId();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    if (deviceId) {
      config.headers['X-Device-Id'] = deviceId;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle token refresh on 401 - try refresh on any 401 when we have a refresh token
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      const refreshToken = localStorage.getItem('refreshToken');

      // Try refresh on any 401 when we have a refresh token (most robust approach)
      if (refreshToken) {
        originalRequest._retry = true;
        try {
          const response = await axios.post(`${API_URL}/auth/refresh`, { refreshToken });
          if (response.data.success) {
            const { accessToken } = response.data.data;
            localStorage.setItem('accessToken', accessToken);
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            return api(originalRequest);
          }
        } catch (refreshError) {
          clearStoredAuth();
          emitAuthRedirect({ reason: 'session-expired' });
          return Promise.reject(refreshError);
        }
      }

      clearStoredAuth();
      emitAuthRedirect({ reason: 'session-expired' });
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  changePassword: (currentPassword, newPassword) =>
    api.post('/auth/change-password', { currentPassword, newPassword }),
};

// User API v2
export const userV2API = {
  getProfile: (userId) => api.get(`/v2/users/${userId}/profile`),
  patchProfile: (userId, data) => api.patch(`/v2/users/${userId}/profile`, data),
  getGoals: (userId) => api.get(`/v2/users/${userId}/goals`),
  patchGoals: (userId, data) => api.patch(`/v2/users/${userId}/goals`, data),
  getHealthContext: (userId) => api.get(`/v2/users/${userId}/health-context`),
  patchHealthContext: (userId, data) => api.patch(`/v2/users/${userId}/health-context`, data),
  getOnboarding: (userId) => api.get(`/v2/users/${userId}/onboarding`),
  patchOnboarding: (userId, data) => api.patch(`/v2/users/${userId}/onboarding`, data),
  getFastingProfile: (userId) => api.get(`/v2/users/${userId}/fasting-profile`),
  patchFastingProfile: (userId, data) => api.patch(`/v2/users/${userId}/fasting-profile`, data),
  getNutritionPreferences: (userId) => api.get(`/v2/users/${userId}/nutrition-preferences`),
  patchNutritionPreferences: (userId, data) => api.patch(`/v2/users/${userId}/nutrition-preferences`, data),
};

export const nutritionV2API = {
  getInsights: (userId, params = {}) =>
    api.get(`/v2/nutrition/${userId}/insights`, { params }),
};

export const fastingV2API = {
  getRecommendations: (userId, params = {}) =>
    api.get(`/v2/fasting/${userId}/recommendations`, { params }),
};

// User API
export const userAPI = {
  getAll: () => api.get('/users'), // Admin only
  get: (userId) => api.get(`/users/${userId}`),
  create: (data) => api.post('/users', data),
  update: (userId, data) => api.put(`/users/${userId}`, data),
  updateWeight: (userId, weight) => api.patch(`/users/${userId}/weight`, { weight }),
  updatePreferences: (userId, preferences) =>
    api.patch(`/users/${userId}/preferences`, preferences),
  delete: (userId) => api.delete(`/users/${userId}`),
};

// Daily Summary API v2
export const dailySummaryAPI = {
  get: (userId, dayKey) => api.get(`/v2/daily-summary/${userId}/${dayKey}`),
  getRange: (userId, startDate, endDate) =>
    api.get(`/v2/daily-summary/${userId}/range?startDate=${startDate}&endDate=${endDate}`),
  put: (userId, dayKey, data) => api.put(`/v2/daily-summary/${userId}/${dayKey}`, data),
  patchSection: (userId, dayKey, section, data) =>
    api.patch(`/v2/daily-summary/${userId}/${dayKey}/${section}`, data),
  addFood: (userId, dayKey, foodEntry) =>
    api.post(`/v2/daily-summary/${userId}/${dayKey}/nutrition/food`, foodEntry),
  removeFood: (userId, dayKey, entryId) =>
    api.delete(`/v2/daily-summary/${userId}/${dayKey}/nutrition/food/${entryId}`),
};

// Daily Metrics API (legacy v1)
export const dailyMetricsAPI = {
  get: (userId, date) => api.get(`/daily-metrics/${userId}/${date}`),
  getRange: (userId, startDate, endDate) =>
    api.get(`/daily-metrics/${userId}/range?startDate=${startDate}&endDate=${endDate}`),
  create: (userId, date, data) => api.post(`/daily-metrics/${userId}/${date}`, data),
  update: (userId, date, data) => api.put(`/daily-metrics/${userId}/${date}`, data),
  updateHabits: (userId, date, habits) =>
    api.patch(`/daily-metrics/${userId}/${date}/habits`, habits),
  updateFasting: (userId, date, fasting) =>
    api.patch(`/daily-metrics/${userId}/${date}/fasting`, fasting),
  addFood: (userId, date, foodEntry) =>
    api.post(`/daily-metrics/${userId}/${date}/food`, foodEntry),
  removeFood: (userId, date, entryId) =>
    api.delete(`/daily-metrics/${userId}/${date}/food/${entryId}`),
};

// Health Data API
export const healthDataAPI = {
  get: (userId, date) => api.get(`/health-data/${userId}/${date}`),
  getRange: (userId, startDate, endDate) =>
    api.get(`/health-data/${userId}/range?startDate=${startDate}&endDate=${endDate}`),
  create: (userId, date, data) => api.post(`/health-data/${userId}/${date}`, data),
  update: (userId, date, data) => api.put(`/health-data/${userId}/${date}`, data),
  delete: (userId, date) => api.delete(`/health-data/${userId}/${date}`),
};

// Achievements API v2
export const achievementsV2API = {
  getTemplates: () => api.get('/v2/achievements/templates'),
  getUserUnlocks: (userId) => api.get(`/v2/achievements/users/${userId}/unlocks`),
  unlock: (userId, data) => api.post(`/v2/achievements/users/${userId}/unlocks`, data),
};

// Achievements API (legacy v1)
export const achievementsAPI = {
  getUserAchievements: (userId) => api.get(`/achievements/user/${userId}`),
  get: (achievementId) => api.get(`/achievements/${achievementId}`),
  create: (data) => api.post('/achievements', data),
  delete: (achievementId) => api.delete(`/achievements/${achievementId}`),
};

// Fasting Protocols API
export const protocolsAPI = {
  getAll: () => api.get('/fasting-protocols'),
  get: (protocolId) => api.get(`/fasting-protocols/${protocolId}`),
  create: (data) => api.post('/fasting-protocols', data),
  update: (protocolId, data) => api.put(`/fasting-protocols/${protocolId}`, data),
  delete: (protocolId) => api.delete(`/fasting-protocols/${protocolId}`),
};

// Account Management API
export const accountAPI = {
  requestDeletionOTP: () => api.post('/account/delete/request-otp'),
  verifyAndDelete: (otp) => api.post('/account/delete/verify', { otp }),
  cancelDeletion: () => api.post('/account/delete/cancel'),
};

// Foods API
export const foodsAPI = {
  list: (params) => api.get('/foods', { params }),
  get: (id) => api.get(`/foods/${id}`),
  create: (data) => api.post('/foods', data),
  update: (id, data) => api.put(`/foods/${id}`, data),
  delete: (id) => api.delete(`/foods/${id}`),
};

// Product submissions API
export const productSubmissionsAPI = {
  lookup: (barcode) => api.get(`/product-submissions/lookup/${barcode}`),
  create: (formData) => api.post('/product-submissions', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  list: (params) => api.get('/product-submissions', { params }),
  verify: (id) => api.patch(`/product-submissions/${id}/verify`),
  reject: (id) => api.patch(`/product-submissions/${id}/reject`),
};

// Support tickets API
export const supportTicketsAPI = {
  create: (formData) => api.post('/support-tickets', formData, { headers: formData.get ? { 'Content-Type': 'multipart/form-data' } : {} }),
  getMine: () => api.get('/support-tickets/mine'),
  list: () => api.get('/support-tickets'),
  update: (id, data) => api.patch(`/support-tickets/${id}`, data),
};

// Admin API
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  getOverviewAnalytics: (params) => api.get('/admin/overview-analytics', { params }),
  getSystemHealth: () => api.get('/admin/system-health'),
  getDetailedHealth: () => api.get('/admin/health/detailed'),
  getRequestMetrics: () => api.get('/admin/health/metrics'),
  getSystemLogs: (params) => api.get('/admin/health/logs', { params }),
  clearSystemLogs: () => api.delete('/admin/health/logs'),
  getUsers: (params) => api.get('/admin/users', { params }),
  getUserProfile: (userId) => api.get(`/admin/users/${userId}/profile`),
  makeAdmin: (userId) => api.post(`/admin/users/${userId}/make-admin`),
  removeAdmin: (userId) => api.post(`/admin/users/${userId}/remove-admin`),
  updateUserStatus: (userId, isActive) => api.patch(`/admin/users/${userId}/status`, { isActive }),
  banUser: (userId, isBanned) => api.patch(`/admin/users/${userId}/ban`, { isBanned }),
  getSubscriptions: (params) => api.get('/admin/subscriptions', { params }),
  getSubscriptionByUser: (userId) => api.get(`/admin/subscriptions/user/${userId}`),
  updateSubscription: (userId, data) => api.patch(`/admin/subscriptions/user/${userId}`, data),
  syncSubscription: (userId) => api.post(`/admin/subscriptions/user/${userId}/sync`),
  refundSubscription: (userId, data) => api.post(`/admin/subscriptions/user/${userId}/refund`, data),
  addAccountCredit: (userId, amountCents) => api.post(`/admin/subscriptions/user/${userId}/credit`, { amountCents }),
};

// Subscriptions API
export const subscriptionsAPI = {
  getMe: () => api.get('/subscriptions/me'),
  sync: (data = {}) => api.post('/subscriptions/sync', {
    ...data,
    deviceId: data.deviceId || getOrCreateWebDeviceId(),
  }),
};

// Waitlist API
export const waitlistAPI = {
  join: (data) => api.post('/waitlist', data),
  list: (params = {}) => api.get('/waitlist', { params }),
  exportCsv: (params = {}) => api.get('/waitlist/export.csv', { params, responseType: 'blob' }),
  downloadCsv: async (params = {}, filename) => {
    const response = await api.get('/waitlist/export.csv', { params, responseType: 'blob' });
    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || `fasttime-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    return response;
  },
};

// Health Check
export const healthCheck = () => api.get('/health');

const defaultSystemHealth = {
  status: 'operational',
  uptimeSeconds: 0,
  services: [
    { name: 'API Services', status: 100 },
    { name: 'Database', status: 100 },
    { name: 'Auth Service', status: 100 },
    { name: 'Email Service', status: 85 },
    { name: 'Storage', status: 100 },
  ],
};

function mapPublicHealthToSystemHealth(data) {
  const dbOk = data?.status === 'OK' && data?.database?.status === 'connected';
  return {
    status: dbOk ? 'operational' : 'degraded',
    uptimeSeconds: data?.uptime || 0,
    services: [
      { name: 'API Services', status: dbOk ? 100 : 72 },
      { name: 'Database', status: dbOk ? 100 : 0 },
      { name: 'Auth Service', status: dbOk ? 100 : 85 },
      { name: 'Email Service', status: dbOk ? 85 : 60 },
      { name: 'Storage', status: dbOk ? 100 : 90 },
    ],
    timestamp: data?.timestamp || new Date().toISOString(),
  };
}

/** Resolves system health from admin stats, admin route, or public /health fallback. */
export async function fetchSystemHealth(statsPayload) {
  if (statsPayload?.systemHealth) return statsPayload.systemHealth;
  try {
    const res = await adminAPI.getSystemHealth();
    return res.data?.data || defaultSystemHealth;
  } catch (_) {
    try {
      const res = await healthCheck();
      return mapPublicHealthToSystemHealth(res.data?.data);
    } catch (_) {
      return defaultSystemHealth;
    }
  }
}

export default api;
